import React, { Component } from "react";

// The 8 canonical blood groups, in the exact string form the
// blood-donations API uses for `blood_group` (e.g. "A+", "O-").
const BLOOD_GROUPS_LIST = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const BLOOD_GROUP_OPTIONS = [
  { value: "", label: "Select" },
  ...BLOOD_GROUPS_LIST.map((g) => ({ value: g, label: g })),
];

// Local-only defaults used until a real thresholds endpoint exists.
const DEFAULT_THRESHOLDS = { critical: 5, low: 15 };

// ─── API helpers ─────────────────────────────────────────────────────────────
const api = {
  get: async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api${path}`);
    return res.json();
  },
  post: async (path, body) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api${path}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );
    return res.json();
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getStockStatus(units, crit, low) {
  if (units <= crit) return "critical";
  if (units <= low) return "low";
  return "ok";
}

const STOCK_STATUS_STYLE = {
  critical: {
    bg: "#FEE2E2",
    text: "#991B1B",
    dot: "#EF4444",
    border: "#FECACA",
  },
  low: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", border: "#FDE68A" },
  ok: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981", border: "#A7F3D0" },
};

// Shelf-life status coming back from the donations API per bag.
const DONATION_STATUS_STYLE = {
  available: { bg: "#D1FAE5", text: "#065F46", border: "#A7F3D0" },
  expiring: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  quarantine: { bg: "#FFE4E6", text: "#9F1239", border: "#FECDD3" },
  discarded: { bg: "#F1F5F9", text: "#475569", border: "#E2E8F0" },
};

class Badge extends Component {
  render() {
    const { status } = this.props;
    const labels = { critical: "Critical", low: "Low stock", ok: "Sufficient" };
    const c = STOCK_STATUS_STYLE[status];
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 20,
          background: c.bg,
          color: c.text,
          border: `1px solid ${c.border}`,
        }}
      >
        {labels[status]}
      </span>
    );
  }
}

class DonationStatusBadge extends Component {
  render() {
    const { status } = this.props;
    const c = DONATION_STATUS_STYLE[status] || DONATION_STATUS_STYLE.available;
    const label = status
      ? status.charAt(0).toUpperCase() + status.slice(1)
      : "—";
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 20,
          background: c.bg,
          color: c.text,
          border: `1px solid ${c.border}`,
        }}
      >
        {label}
      </span>
    );
  }
}

class Modal extends Component {
  render() {
    const { open, title, onClose, children } = this.props;
    if (!open) return null;
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          background: "rgba(15,23,42,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "1.75rem",
            width: 400,
            maxWidth: "90vw",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0F172A",
                margin: 0,
              }}
            >
              {title}
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "#F1F5F9",
                border: "none",
                borderRadius: 8,
                width: 28,
                height: 28,
                cursor: "pointer",
                color: "#64748B",
                fontSize: 16,
              }}
            >
              ×
            </button>
          </div>
          {children}
        </div>
      </div>
    );
  }
}

class Field extends Component {
  render() {
    const { label, children } = this.props;
    return (
      <div style={{ marginBottom: 12 }}>
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: "#475569",
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </label>
        {children}
      </div>
    );
  }
}

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 8,
  border: "1.5px solid #E2E8F0",
  fontSize: 14,
  color: "#0F172A",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

class Select extends Component {
  render() {
    const { label, options, value, onChange } = this.props;
    return (
      <Field label={label}>
        <select value={value} onChange={onChange} style={inputStyle}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }
}

class Input extends Component {
  render() {
    const { label, ...props } = this.props;
    return (
      <Field label={label}>
        <input {...props} style={inputStyle} />
      </Field>
    );
  }
}

class Btn extends Component {
  render() {
    const {
      children,
      variant = "primary",
      full,
      onClick,
      disabled,
      style: s,
    } = this.props;
    const variants = {
      primary: { background: "#C0392B", color: "#fff" },
      secondary: { background: "#F1F5F9", color: "#475569" },
      danger: { background: "#C0392B", color: "#fff" },
    };
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          padding: "9px 18px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          border: "none",
          opacity: disabled ? 0.6 : 1,
          width: full ? "100%" : "auto",
          ...variants[variant],
          ...s,
        }}
      >
        {children}
      </button>
    );
  }
}

class SubHeader extends Component {
  render() {
    const { tabs, activeTab, onChange } = this.props;
    return (
      <div
        style={{
          position: "fixed",
          top: 70,
          left: 0,
          right: 0,
          zIndex: 900,
          background: "#fff",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          gap: 4,
          padding: "0 24px 0 60px",
          height: 46,
          alignItems: "center",
        }}
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onChange(tab.key)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px 14px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: active ? "#C0392B" : "#64748B",
                borderBottom: active
                  ? "2px solid #C0392B"
                  : "2px solid transparent",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
class ManageDonors extends Component {
  constructor(props) {
    super(props);
    this.state = {
      donations: [],
      loading: true,
      activeTab: "overview",
      toast: null,
      submitting: false,

      // Local-only thresholds per blood group (no backend yet — see note in chat).
      thresholds: {},

      addModal: false,
      dispenseModal: false,
      searchModal: false,
      threshModal: false,

      searchGroup: "all",

      form: {
        blood_group: "",
        units: "",
        note: "",
        critical_threshold: "",
        low_threshold: "",
        donor_id: "",
        donation_date: new Date().toISOString().split("T")[0],
      },
    };

    this.toastTimeout = null;

    this.showToast = this.showToast.bind(this);
    this.fetchDonations = this.fetchDonations.bind(this);
    this.openModal = this.openModal.bind(this);
    this.handleGroupChange = this.handleGroupChange.bind(this);
    this.addDonation = this.addDonation.bind(this);
    this.dispense = this.dispense.bind(this);
    this.saveThresholds = this.saveThresholds.bind(this);
    this.computeStock = this.computeStock.bind(this);
  }

  componentDidMount() {
    this.fetchDonations().finally(() => this.setState({ loading: false }));
  }

  componentWillUnmount() {
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
  }

  showToast(msg, type = "success") {
    this.setState({ toast: { msg, type } });
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => this.setState({ toast: null }), 3500);
  }

  async fetchDonations() {
    try {
      const res = await api.get("/blood-donations");
      if (res.status === "success") {
        this.setState({ donations: res.data || [] });
      } else {
        this.showToast(res.message || "Failed to load donations.", "error");
      }
    } catch (err) {
      this.showToast("Network error loading donations.", "error");
    }
  }

  // Derive per-group stock from the raw donations list. A bag counts as
  // "in stock" until it's actually discarded — expiring/quarantine bags
  // still count, since they're physically still on the shelf.
  computeStock() {
    const { donations, thresholds } = this.state;
    const counts = {};
    BLOOD_GROUPS_LIST.forEach((g) => {
      counts[g] = 0;
    });
    donations.forEach((d) => {
      if (d.status !== "discarded" && counts.hasOwnProperty(d.blood_group)) {
        counts[d.blood_group] += 1;
      }
    });
    return BLOOD_GROUPS_LIST.map((g) => {
      const th = thresholds[g] || DEFAULT_THRESHOLDS;
      return {
        id: g,
        blood_group: g,
        units_available: counts[g],
        critical_threshold: th.critical,
        low_threshold: th.low,
      };
    });
  }

  openModal(modal, groupId = null) {
    const { thresholds } = this.state;
    const g = groupId || "";
    const th = thresholds[g] || DEFAULT_THRESHOLDS;
    const form = {
      blood_group: g,
      units: "",
      note: "",
      critical_threshold: String(th.critical),
      low_threshold: String(th.low),
      donor_id: "",
      donation_date: new Date().toISOString().split("T")[0],
    };

    const modalState = { form };
    if (modal === "add") modalState.addModal = true;
    if (modal === "dispense") modalState.dispenseModal = true;
    if (modal === "search") modalState.searchModal = true;
    if (modal === "thresh") modalState.threshModal = true;

    this.setState(modalState);
  }

  handleGroupChange(e) {
    const g = e.target.value;
    const th = this.state.thresholds[g] || DEFAULT_THRESHOLDS;
    this.setState((prev) => ({
      form: {
        ...prev.form,
        blood_group: g,
        critical_threshold: String(th.critical),
        low_threshold: String(th.low),
      },
    }));
  }

  // ── Add a donation (replaces old /stock/add) ─────────────────────────────
  async addDonation() {
    const { form } = this.state;
    if (!form.donor_id || !form.blood_group || !form.donation_date) {
      this.showToast("Donor ID, blood group, and date are required.", "error");
      return;
    }
    this.setState({ submitting: true });
    try {
      const res = await api.post("/blood-donations/scan", {
        donor_id: form.donor_id,
        blood_group: form.blood_group,
        donation_date: form.donation_date,
      });
      if (res.status === "success") {
        this.showToast(res.message || "Blood bag added.");
        this.setState({ addModal: false });
        await this.fetchDonations();
      } else {
        this.showToast(res.message || "Failed to add donation.", "error");
      }
    } catch (err) {
      this.showToast("Network error while adding donation.", "error");
    }
    this.setState({ submitting: false });
  }

  // ── Dispense = discard the oldest available bags for a group (FIFO) ─────
  // Uses the existing POST /blood-donations/:id/discard endpoint.
  // Note: that endpoint has no note/reason field, so `form.note` is
  // currently just local context for the person doing the dispensing.
  async dispense() {
    const { form, donations } = this.state;
    const unitsToDispense = parseInt(form.units, 10);

    if (!form.blood_group || !unitsToDispense || unitsToDispense < 1) {
      this.showToast("Choose a blood group and a valid unit count.", "error");
      return;
    }

    const available = donations
      .filter(
        (d) => d.blood_group === form.blood_group && d.status !== "discarded",
      )
      .sort((a, b) => new Date(a.donation_date) - new Date(b.donation_date)); // oldest first

    if (available.length < unitsToDispense) {
      this.showToast(
        `Only ${available.length} unit(s) of ${form.blood_group} available.`,
        "error",
      );
      return;
    }

    const toDiscard = available.slice(0, unitsToDispense);
    this.setState({ submitting: true });
    try {
      const results = await Promise.all(
        toDiscard.map((d) => api.post(`/blood-donations/${d.id}/discard`, {})),
      );
      const failed = results.filter((r) => r.status !== "success");
      if (failed.length) {
        this.showToast(
          `${failed.length} of ${toDiscard.length} bag(s) failed to dispense.`,
          "error",
        );
      } else {
        this.showToast(
          `Dispensed ${unitsToDispense} unit(s) of ${form.blood_group}.`,
        );
        this.setState({ dispenseModal: false });
      }
      await this.fetchDonations();
    } catch (err) {
      this.showToast("Network error while dispensing.", "error");
    }
    this.setState({ submitting: false });
  }

  // ── Thresholds are local-only for now (no backend endpoint exists for
  // donation-derived stock thresholds yet) ─────────────────────────────────
  saveThresholds() {
    const { form, thresholds } = this.state;
    const crit = parseInt(form.critical_threshold, 10);
    const low = parseInt(form.low_threshold, 10);
    if (!form.blood_group || Number.isNaN(crit) || Number.isNaN(low)) {
      this.showToast("Enter valid thresholds.", "error");
      return;
    }
    this.setState({
      thresholds: {
        ...thresholds,
        [form.blood_group]: { critical: crit, low },
      },
      threshModal: false,
    });
    this.showToast(
      "Thresholds updated (locally — not yet saved to a backend).",
    );
  }

  render() {
    const {
      donations,
      loading,
      activeTab,
      toast,
      submitting,
      addModal,
      dispenseModal,
      searchModal,
      threshModal,
      form,
      searchGroup,
    } = this.state;

    const stock = this.computeStock();

    // Computed stats

    const totalUnits = stock.reduce((a, s) => a + s.units_available, 0);
    const criticalGroups = stock.filter(
      (s) =>
        getStockStatus(
          s.units_available,
          s.critical_threshold,
          s.low_threshold,
        ) === "critical",
    );
    const lowGroups = stock.filter(
      (s) =>
        getStockStatus(
          s.units_available,
          s.critical_threshold,
          s.low_threshold,
        ) === "low",
    );
    const expiringSoon = donations.filter(
      (d) => d.status === "expiring",
    ).length;
    const discardedTotal = donations.filter(
      (d) => d.status === "discarded",
    ).length;
    const availableInStock = donations.filter(
      (d) => !["discarded", "issued", "quarantine"].includes(
        String(d.status || "").toLowerCase().trim()
      )
    ).length;

    const filteredStock =
      searchGroup === "all"
        ? stock
        : stock.filter((s) => s.blood_group === searchGroup);
    const currentStock = stock.find((s) => s.blood_group === form.blood_group);

    const recentDonations = [...donations].sort(
      (a, b) => new Date(b.donation_date) - new Date(a.donation_date),
    );

    // const TABS = [
    //   { key: "overview", label: "Overview", icon: "📊" },
    //   { key: "stock", label: "Stock", icon: "🩸" },
    //   { key: "logs", label: "Donations", icon: "📋" },
    //   { key: "thresholds", label: "Thresholds", icon: "⚙️" },
    // ];

    // const SUBHEADER_TABS = TABS; // show all four tabs: Overview, Stock, Logs, Thresholds
    // const showSubHeader = true; // sub-header is visible across the whole Dashboard page

    if (loading)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            fontFamily: "Inter, system-ui, sans-serif",
            color: "#64748B",
          }}
        >
          Loading blood bank data...
        </div>
      );

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#F8FAFC",
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        {/* Toast */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: 20,
              right: 20,
              zIndex: 100,
              padding: "12px 20px",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 500,
              background: toast.type === "error" ? "#FEE2E2" : "#D1FAE5",
              color: toast.type === "error" ? "#991B1B" : "#065F46",
              border: `1px solid ${toast.type === "error" ? "#FECACA" : "#A7F3D0"}`,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            {toast.type === "error" ? "⚠ " : "✓ "}
            {toast.msg}
          </div>
        )}

        {/* Sub-header */}
        {/* <SubHeader
          tabs={TABS}
          activeTab={activeTab}
          onChange={key => this.setState({ activeTab: key })}
        /> */}

        {/* Main */}
        <div
          style={{
            marginLeft: 60,
            marginRight: 20,
            padding: "1rem 1rem 1rem 0",
            paddingTop: "calc(50px + 1rem + 40px)",
          }}
        >
          {/* ── OVERVIEW ── */}
          {/* ── OVERVIEW ── */}
          {activeTab === "overview" && (
            <>
              {/* Summary stats including quarantine, issued, discarded */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 16,
                  marginBottom: 28,
                  marginTop: 24,
                }}
              >
                {[
                  {
                    label: "Total units",
                    value: totalUnits.toLocaleString(),
                    sub: "across all groups",
                    color: "#0F172A",
                  },
                  {
                    label: "Expiring soon",
                    value: expiringSoon,
                    sub: "bags nearing shelf-life limit",
                    color: "#0F172A",
                  },
                  {
                    label: "Critical groups",
                    value: criticalGroups.length,
                    sub:
                      criticalGroups.map((s) => s.blood_group).join(", ") ||
                      "None",
                    color: "#C0392B",
                  },
                  {
                    label: "Low stock alerts",
                    value: lowGroups.length,
                    sub:
                      lowGroups.map((s) => s.blood_group).join(", ") || "None",
                    color: "#B45309",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      padding: "1.25rem",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#64748B",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: 8,
                      }}
                    >
                      {s.label}
                    </div>
                    <div
                      style={{ fontSize: 28, fontWeight: 700, color: s.color }}
                    >
                      {s.value}
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}
                    >
                      {s.sub}
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional stats: Quarantine, Issued, Discarded */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                  gap: 16,
                  marginBottom: 28,
                }}
              >
                {[
                  {
                    label: "Available in Stock",
                    value: availableInStock,
                    color: "#16A34A",
                    bg: "#DCFCE7",
                    icon: "🩸",
                  },
                  {
                    label: "Quarantine",
                    value: donations.filter((d) => d.status === "quarantine")
                      .length,
                    color: "#DC3545",
                    bg: "#FEE2E2",
                    icon: "⚠️",
                  },
                  {
                    label: "Issued",
                    value: donations.filter((d) => d.status === "issued")
                      .length,
                    color: "#6B7280",
                    bg: "#F3F4F6",
                    icon: "📤",
                  },
                  {
                    label: "Discarded",
                    value: donations.filter((d) => d.status === "discarded")
                      .length,
                    color: "#6B7280",
                    bg: "#F3F4F6",
                    icon: "🗑️",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: s.bg,
                      borderRadius: 12,
                      padding: "1rem",
                      border: `1px solid ${s.color}33`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#64748B",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span>{s.icon}</span> {s.label}
                    </div>
                    <div
                      style={{ fontSize: 28, fontWeight: 700, color: s.color }}
                    >
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  border: "1px solid #E2E8F0",
                  padding: "1.5rem",
                  marginBottom: 24,
                }}
              >
                <h2
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#0F172A",
                    marginBottom: 16,
                  }}
                >
                  Blood stock — all 8 groups
                </h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                    gap: 12,
                  }}
                >
                  {stock.map((s) => {
                    const st = getStockStatus(
                      s.units_available,
                      s.critical_threshold,
                      s.low_threshold,
                    );
                    const c = STOCK_STATUS_STYLE[st];
                    const maxUnits = Math.max(
                      1,
                      ...stock.map((x) => x.units_available),
                    );
                    const pct = Math.min(
                      100,
                      Math.round((s.units_available / maxUnits) * 100),
                    );

                    // Get counts for this blood group
                    const quarantineCount = donations.filter(
                      (d) =>
                        d.blood_group === s.blood_group &&
                        d.status === "quarantine",
                    ).length;
                    const issuedCount = donations.filter(
                      (d) =>
                        d.blood_group === s.blood_group &&
                        d.status === "issued",
                    ).length;
                    const discardedCount = donations.filter(
                      (d) =>
                        d.blood_group === s.blood_group &&
                        d.status === "discarded",
                    ).length;

                    return (
                      <div
                        key={s.id}
                        style={{
                          border: `1.5px solid ${c.border}`,
                          borderRadius: 10,
                          padding: "1rem",
                          background: c.bg,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 800,
                              color: c.text,
                            }}
                          >
                            {s.blood_group}
                          </span>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: c.dot,
                            }}
                          />
                        </div>

                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: c.text,
                            marginBottom: 6,
                          }}
                        >
                          {s.units_available}
                        </div>

                        <div
                          style={{
                            height: 4,
                            background: "rgba(0,0,0,0.1)",
                            borderRadius: 2,
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: "100%",
                              background: c.dot,
                              borderRadius: 2,
                            }}
                          />
                        </div>

                        <Badge status={st} />

                        {/* Show quarantine count if > 0 */}
                        {(quarantineCount > 0 ||
                          issuedCount > 0 ||
                          discardedCount > 0) && (
                            <div
                              style={{
                                marginTop: 6,
                                paddingTop: 6,
                                borderTop: "1px solid rgba(0,0,0,0.06)",
                                fontSize: 10,
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                color: "#64748B",
                              }}
                            >
                              {quarantineCount > 0 && (
                                <span
                                  style={{ color: "#DC3545", fontWeight: 600 }}
                                >
                                  ⚠️ {quarantineCount} quarantine
                                </span>
                              )}
                              {issuedCount > 0 && (
                                <span style={{ color: "#6B7280" }}>
                                  📤 {issuedCount} issued
                                </span>
                              )}
                              {discardedCount > 0 && (
                                <span style={{ color: "#6B7280" }}>
                                  🗑️ {discardedCount} discarded
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* ── STOCK TABLE ── */}
          {activeTab === "stock" && (
            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #E2E8F0",
                overflow: "hidden",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F8FAFC" }}>
                    {[
                      "Blood group",
                      "Units available",
                      "Critical threshold",
                      "Low threshold",
                      "Status",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          borderBottom: "1px solid #E2E8F0",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stock.map((s, i) => {
                    const st = getStockStatus(
                      s.units_available,
                      s.critical_threshold,
                      s.low_threshold,
                    );
                    return (
                      <tr
                        key={s.id}
                        style={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}
                      >
                        <td
                          style={{
                            padding: "14px 16px",
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#0F172A",
                          }}
                        >
                          {s.blood_group}
                        </td>
                        <td style={{ padding: "14px 16px", fontWeight: 600 }}>
                          {s.units_available}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#64748B" }}>
                          {s.critical_threshold}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#64748B" }}>
                          {s.low_threshold}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <Badge status={st} />
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Btn
                              variant="secondary"
                              style={{ padding: "5px 10px", fontSize: 12 }}
                              onClick={() =>
                                this.openModal("dispense", s.blood_group)
                              }
                            >
                              Dispense
                            </Btn>
                            <Btn
                              variant="secondary"
                              style={{ padding: "5px 10px", fontSize: 12 }}
                              onClick={() =>
                                this.openModal("thresh", s.blood_group)
                              }
                            >
                              Thresholds
                            </Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── DONATIONS (was Logs) ── */}
          {activeTab === "logs" && (
            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #E2E8F0",
                overflow: "hidden",
              }}
            >
              {recentDonations.length === 0 ? (
                <div
                  style={{
                    padding: "3rem",
                    textAlign: "center",
                    color: "#94A3B8",
                  }}
                >
                  No donations recorded yet.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                      {[
                        "Date",
                        "Blood group",
                        "Donor ID",
                        "Bag serial",
                        "Shelf life",
                        "Status",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "12px 16px",
                            textAlign: "left",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#475569",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid #E2E8F0",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentDonations.map((d, i) => (
                      <tr
                        key={d.id}
                        style={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}
                      >
                        <td
                          style={{
                            padding: "12px 16px",
                            fontSize: 13,
                            color: "#64748B",
                          }}
                        >
                          {d.donation_date}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            fontWeight: 700,
                            color: "#0F172A",
                          }}
                        >
                          {d.blood_group}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>
                          {d.donor_id}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            fontSize: 13,
                            color: "#64748B",
                          }}
                        >
                          {d.bag_serial || "—"}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>
                          {d.percent != null ? `${d.percent}%` : "—"}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <DonationStatusBadge status={d.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── THRESHOLDS ── */}
          {activeTab === "thresholds" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div
                style={{
                  background: "#FEF3C7",
                  border: "1px solid #FDE68A",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 4,
                  fontSize: 12,
                  color: "#92400E",
                }}
              >
                Thresholds are currently local to your browser session only —
                hook up a backend endpoint to persist them across reloads.
              </div>
              {stock.map((s) => {
                const st = getStockStatus(
                  s.units_available,
                  s.critical_threshold,
                  s.low_threshold,
                );
                const c = STOCK_STATUS_STYLE[st];
                return (
                  <div
                    key={s.id}
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "1px solid #E2E8F0",
                      padding: "1.25rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 10,
                        background: c.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 14,
                        color: c.text,
                        flexShrink: 0,
                      }}
                    >
                      {s.blood_group}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 6,
                        }}
                      >
                        <span style={{ fontSize: 13, color: "#64748B" }}>
                          Critical: <strong>{s.critical_threshold}</strong>{" "}
                          &nbsp;|&nbsp; Low: <strong>{s.low_threshold}</strong>{" "}
                          &nbsp;|&nbsp; Current:{" "}
                          <strong>{s.units_available}</strong>
                        </span>
                        <Badge status={st} />
                      </div>
                      <div style={{ display: "flex", gap: 4 }}>
                        <div
                          style={{
                            flex: s.critical_threshold || 1,
                            height: 6,
                            background: "#FEE2E2",
                            borderRadius: "4px 0 0 4px",
                          }}
                        />
                        <div
                          style={{
                            flex:
                              Math.max(
                                0,
                                s.low_threshold - s.critical_threshold,
                              ) || 1,
                            height: 6,
                            background: "#FEF3C7",
                          }}
                        />
                        <div
                          style={{
                            flex:
                              Math.max(
                                0,
                                s.units_available - s.low_threshold,
                              ) || 1,
                            height: 6,
                            background: "#D1FAE5",
                            borderRadius: "0 4px 4px 0",
                          }}
                        />
                      </div>
                    </div>
                    <Btn
                      variant="secondary"
                      style={{ fontSize: 12, padding: "7px 14px" }}
                      onClick={() => this.openModal("thresh", s.blood_group)}
                    >
                      Edit thresholds
                    </Btn>
                    <Btn
                      variant="secondary"
                      style={{ fontSize: 12, padding: "7px 14px" }}
                      onClick={() => this.openModal("thresh", s.blood_group)}
                    >
                      Edit thresholds
                    </Btn>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── MODALS ── */}

        {/* Add */}
        <Modal
          open={addModal}
          title="Add blood bag"
          onClose={() => this.setState({ addModal: false })}
        >
          <Select
            label="Blood group"
            options={BLOOD_GROUP_OPTIONS}
            value={form.blood_group}
            onChange={this.handleGroupChange}
          />
          <Input
            label="Donor ID"
            placeholder="e.g. AWT-123456"
            value={form.donor_id}
            onChange={(e) =>
              this.setState((prev) => ({
                form: { ...prev.form, donor_id: e.target.value },
              }))
            }
          />
          <Input
            label="Date of donation"
            type="date"
            value={form.donation_date}
            onChange={(e) =>
              this.setState((prev) => ({
                form: { ...prev.form, donation_date: e.target.value },
              }))
            }
          />
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Btn
              variant="secondary"
              full
              onClick={() => this.setState({ addModal: false })}
            >
              Cancel
            </Btn>
            <Btn full disabled={submitting} onClick={this.addDonation}>
              {submitting ? "Saving…" : "Add record"}
            </Btn>
          </div>
        </Modal>

        {/* Dispense */}
        <Modal
          open={dispenseModal}
          title="Dispense blood"
          onClose={() => this.setState({ dispenseModal: false })}
        >
          <Select
            label="Blood group"
            options={BLOOD_GROUP_OPTIONS}
            value={form.blood_group}
            onChange={this.handleGroupChange}
          />
          {currentStock && (
            <p
              style={{
                fontSize: 12,
                color: "#64748B",
                marginBottom: 12,
                marginTop: -4,
              }}
            >
              Available: <strong>{currentStock.units_available} units</strong>{" "}
              (oldest bags go out first)
            </p>
          )}
          <Input
            label="Units to dispense"
            type="number"
            min="1"
            placeholder="e.g. 2"
            value={form.units}
            onChange={(e) =>
              this.setState((prev) => ({
                form: { ...prev.form, units: e.target.value },
              }))
            }
          />
          <Input
            label="Recipient / note (not saved yet)"
            placeholder="Patient ID, ward, request #…"
            value={form.note}
            onChange={(e) =>
              this.setState((prev) => ({
                form: { ...prev.form, note: e.target.value },
              }))
            }
          />
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Btn
              variant="secondary"
              full
              onClick={() => this.setState({ dispenseModal: false })}
            >
              Cancel
            </Btn>
            <Btn
              variant="danger"
              full
              disabled={submitting}
              onClick={this.dispense}
            >
              {submitting ? "Dispensing…" : "Dispense"}
            </Btn>
          </div>
        </Modal>

        {/* Search */}
        <Modal
          open={searchModal}
          title="Search & filter stock"
          onClose={() => this.setState({ searchModal: false })}
        >
          <Field label="Filter by blood group">
            <select
              value={searchGroup}
              onChange={(e) => this.setState({ searchGroup: e.target.value })}
              style={inputStyle}
            >
              <option value="all">All groups</option>
              {BLOOD_GROUPS_LIST.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {filteredStock.map((s) => {
              const st = getStockStatus(
                s.units_available,
                s.critical_threshold,
                s.low_threshold,
              );
              const c = STOCK_STATUS_STYLE[st];
              return (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    background: c.bg,
                    borderRadius: 8,
                    border: `1px solid ${c.border}`,
                  }}
                >
                  <span style={{ fontWeight: 700, color: c.text }}>
                    {s.blood_group}
                  </span>
                  <span style={{ fontSize: 13, color: c.text }}>
                    {s.units_available} units
                  </span>
                  <Badge status={st} />
                </div>
              );
            })}
          </div>
          <Btn
            variant="secondary"
            full
            onClick={() => this.setState({ searchModal: false })}
          >
            Close
          </Btn>
        </Modal>

        {/* Thresholds */}
        <Modal
          open={threshModal}
          title={`Set thresholds — ${currentStock?.blood_group || ""}`}
          onClose={() => this.setState({ threshModal: false })}
        >
          <Select
            label="Blood group"
            options={BLOOD_GROUP_OPTIONS}
            value={form.blood_group}
            onChange={this.handleGroupChange}
          />
          <Input
            label="Critical threshold (units)"
            type="number"
            min="0"
            value={form.critical_threshold}
            onChange={(e) =>
              this.setState((prev) => ({
                form: { ...prev.form, critical_threshold: e.target.value },
              }))
            }
          />
          <Input
            label="Low stock threshold (units)"
            type="number"
            min="0"
            value={form.low_threshold}
            onChange={(e) =>
              this.setState((prev) => ({
                form: { ...prev.form, low_threshold: e.target.value },
              }))
            }
          />
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Btn
              variant="secondary"
              full
              onClick={() => this.setState({ threshModal: false })}
            >
              Cancel
            </Btn>
            <Btn full disabled={submitting} onClick={this.saveThresholds}>
              {submitting ? "Saving…" : "Save thresholds"}
            </Btn>
          </div>
        </Modal>
      </div>
    );
  }
}

export default ManageDonors;

